import { Component, Input, Output, EventEmitter, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

export interface RenfortSentData {
  siteName: string;
  siteCount: number;
}

interface RenfortSite {
  nom: string;
  statut: 'critique' | 'moderee' | 'fluide';
  selected: boolean;
}

@Component({
  selector: 'app-renfort-screen',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './renfort-screen.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./renfort-screen.component.scss'],
})
export class RenfortScreenComponent implements OnInit {
  @Input({ required: true }) siteName!: string;
  @Input({ required: true }) siteStatut!: string;
  @Input() tensionMinutes: number | null = null;
  @Output() sent = new EventEmitter<RenfortSentData>();

  readonly message = signal('');

  readonly sites = signal<RenfortSite[]>([
    { nom: 'Saint-Herblain', statut: 'moderee', selected: false },
    { nom: 'Rezé',           statut: 'fluide',  selected: true  },
    { nom: 'Nantes Beaujoire', statut: 'fluide', selected: true },
    { nom: 'Carquefou',     statut: 'moderee',  selected: false },
    { nom: 'Orvault',       statut: 'fluide',   selected: true  },
    { nom: 'Vertou',        statut: 'fluide',   selected: true  },
  ]);

  ngOnInit() {
    const statutLabel: Record<string, string> = {
      critique: 'tension critique',
      moderee: 'tension modérée',
      fluide: 'situation normale',
      ferme: '',
    };
    const niveau = statutLabel[this.siteStatut] ?? 'tension';
    const duree = this.tensionMinutes !== null ? ` depuis ${this.tensionMinutes} min` : '';

    this.message.set(
      `Bonjour à tous,\n\nLe site ${this.siteName} est en ${niveau}${duree}. Nous avons besoin d'un renfort temporaire.\n\nMerci de confirmer votre disponibilité dès que possible.`
    );
    this.sites.update(list => list.filter(s => s.nom !== this.siteName));
  }

  toggleSite(nom: string) {
    this.sites.update(list => list.map(s => (s.nom === nom ? { ...s, selected: !s.selected } : s)));
  }

  selectedCount() {
    return this.sites().filter(s => s.selected).length;
  }

  send() {
    if (this.selectedCount() === 0) return;
    this.sent.emit({ siteName: this.siteName, siteCount: this.selectedCount() });
  }

  statutLabel(statut: string): string {
    return (
      ({ critique: 'Critique', moderee: 'En tension', fluide: 'Disponible' } as Record<string, string>)[statut] ?? ''
    );
  }

  statutKey(statut: string): string {
    return (
      ({ critique: 'critique', moderee: 'tension', fluide: 'fluide' } as Record<string, string>)[statut] ?? 'fluide'
    );
  }

  tensionKey(): string {
    return (
      ({ critique: 'critique', moderee: 'tension', fluide: 'fluide', ferme: 'fluide' } as Record<string, string>)[
        this.siteStatut
      ] ?? 'tension'
    );
  }
}